import React, { useEffect, useState } from 'react';
import { adminApi } from '../api';
import { Trophy, Plus, PencilSimple, Trash, ChartBar, X, Image as ImageIcon, UploadSimple } from '@phosphor-icons/react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const SCOPES = [
  { v: 'CF', label: 'Clientes' },
  { v: 'AF', label: 'Afiliados' },
  { v: 'PR', label: 'Embaixadores' },
  { v: 'CS', label: 'Consultores' },
];
const MODES = [
  { v: 'encomenda', label: 'Por data da encomenda', help: 'Conta encomendas geradas a partir do início, com pagamento até ao fim.' },
  { v: 'pagamento', label: 'Por data de pagamento', help: 'Conta os pagamentos que ocorrem entre o início e o fim.' },
];
const eur = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const EMPTY = { nome: '', premio: '', descricao: '', ambito: [], valor_objetivo: '', data_inicio: '', data_fim: '', ativo: true, modo: 'encomenda', imagens: [], imagens_urls: [] };
const imgSrc = (u) => (u?.startsWith('http') ? u : `${BACKEND}${u}`);

function Form({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, valor_objetivo: initial?.valor_objetivo ?? '', imagens: initial?.imagens || [], imagens_urls: initial?.imagens_urls || [] });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const toggle = (v) => setF((s) => ({ ...s, ambito: s.ambito.includes(v) ? s.ambito.filter((x) => x !== v) : [...s.ambito, v] }));
  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = 3 - f.imagens.length;
    if (room <= 0) { setErr('Máximo de 3 imagens por incentivo.'); return; }
    setErr(''); setUploading(true);
    try {
      for (const file of files.slice(0, room)) {
        const r = await adminApi.incentivosUploadImage(file);
        setF((s) => ({ ...s, imagens: [...s.imagens, r.name], imagens_urls: [...s.imagens_urls, r.url] }));
      }
    } catch (ex) { setErr(ex.message || 'Erro a carregar imagem'); } finally { setUploading(false); }
  };
  const removeImage = (i) => setF((s) => ({
    ...s,
    imagens: s.imagens.filter((_, idx) => idx !== i),
    imagens_urls: s.imagens_urls.filter((_, idx) => idx !== i),
  }));
  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      await onSave({ ...f, valor_objetivo: Number(f.valor_objetivo || 0), imagens: f.imagens });
    } catch (e) { setErr(e.message || 'Erro'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" data-testid="incentivo-form-modal">
      <div className="bg-white rounded-xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-[#0F1829]">{initial?.id ? 'Editar' : 'Nova'} campanha</h2>
          <button onClick={onCancel} data-testid="incentivo-form-close"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <label className="block text-sm">Nome
            <input data-testid="incentivo-nome" className="mt-1 w-full border rounded px-3 py-2" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} />
          </label>
          <label className="block text-sm">Prémio (descritivo)
            <input data-testid="incentivo-premio" className="mt-1 w-full border rounded px-3 py-2" placeholder="Ex.: Fim de semana em Paris" value={f.premio} onChange={(e) => setF({ ...f, premio: e.target.value })} />
          </label>
          <label className="block text-sm">Descrição
            <textarea className="mt-1 w-full border rounded px-3 py-2" rows={2} value={f.descricao} onChange={(e) => setF({ ...f, descricao: e.target.value })} />
          </label>
          <div className="text-sm">Âmbito
            <div className="flex flex-wrap gap-2 mt-1">
              {SCOPES.map((s) => (
                <button key={s.v} type="button" onClick={() => toggle(s.v)} data-testid={`incentivo-ambito-${s.v}`}
                  className={`px-3 py-1.5 rounded-full border text-sm ${f.ambito.includes(s.v) ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'bg-white text-[#0F1829] border-[#E7E2D6]'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block text-sm">Valor-objetivo (€)
            <input data-testid="incentivo-objetivo" type="number" className="mt-1 w-full border rounded px-3 py-2" value={f.valor_objetivo} onChange={(e) => setF({ ...f, valor_objetivo: e.target.value })} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">Início
              <input data-testid="incentivo-inicio" type="date" className="mt-1 w-full border rounded px-3 py-2" value={f.data_inicio} onChange={(e) => setF({ ...f, data_inicio: e.target.value })} />
            </label>
            <label className="block text-sm">Fim
              <input data-testid="incentivo-fim" type="date" className="mt-1 w-full border rounded px-3 py-2" value={f.data_fim} onChange={(e) => setF({ ...f, data_fim: e.target.value })} />
            </label>
          </div>
          <div className="text-sm">Modo de contagem
            <div className="flex flex-col gap-1.5 mt-1">
              {MODES.map((m) => (
                <label key={m.v} className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer ${f.modo === m.v ? 'border-[#0F1829] bg-[#FAF8F2]' : 'border-[#E7E2D6]'}`} data-testid={`incentivo-modo-${m.v}`}>
                  <input type="radio" name="modo" className="mt-1 accent-[#0F1829]" checked={f.modo === m.v} onChange={() => setF({ ...f, modo: m.v })} />
                  <span>
                    <span className="font-medium text-[#0F1829]">{m.label}</span>
                    <span className="block text-xs text-[#6B7280]">{m.help}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="text-sm">Imagens promocionais <span className="text-[#978D78] text-xs">(até 3 — aparecem no detalhe do incentivo)</span>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {f.imagens_urls.map((u, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E7E2D6]" data-testid={`incentivo-img-${i}`}>
                  <img src={imgSrc(u)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} data-testid={`incentivo-img-remove-${i}`}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white grid place-items-center"><X size={12} /></button>
                </div>
              ))}
              {f.imagens.length < 3 && (
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E7E2D6] grid place-items-center cursor-pointer text-[#978D78] hover:border-[#0F1829] hover:text-[#0F1829]" data-testid="incentivo-img-add">
                  {uploading ? <span className="text-[10px]">A enviar…</span> : <UploadSimple size={20} />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onPickImages} disabled={uploading} />
                </label>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.ativo} onChange={(e) => setF({ ...f, ativo: e.target.checked })} /> Campanha ativa
          </label>
          {err && <p className="text-red-600 text-sm">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm border rounded">Cancelar</button>
            <button onClick={submit} disabled={saving} data-testid="incentivo-save" className="px-4 py-2 text-sm bg-[#0F1829] text-white rounded disabled:opacity-50">
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Leaderboard({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.incentivosLeaderboard(id).then(setData).catch(() => setData({ leaderboard: [] })); }, [id]);
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" data-testid="incentivo-leaderboard-modal">
      <div className="bg-white rounded-xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-[#0F1829]">Classificação {data?.campanha ? `— ${data.campanha.nome}` : ''}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {data?.campanha && <p className="text-sm text-[#6B7280] mb-3">Objetivo {eur(data.campanha.valor_objetivo)} · {data.atingiram} atingiram</p>}
        {!data ? <p>A carregar…</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#978D78] border-b"><th className="py-2">#</th><th>Nome</th><th>Tipo</th><th className="text-right">Pessoais</th><th className="text-right">Diretas</th><th className="text-right">Total</th><th></th></tr></thead>
            <tbody>
              {data.leaderboard.map((r, i) => (
                <tr key={r.ID_Cliente} className={`border-b border-[#F1EEE6] ${r.atingiu ? 'bg-emerald-50' : ''}`} data-testid={`incentivo-board-row-${r.ID_Cliente}`}>
                  <td className="py-2">{i + 1}</td><td>{r.Nome}</td><td>{r.Tipo}</td>
                  <td className="text-right">{eur(r.pessoal)}</td><td className="text-right">{eur(r.direto)}</td>
                  <td className="text-right font-semibold">{eur(r.total)}</td>
                  <td className="text-right">{r.atingiu ? <span className="text-emerald-600">✓</span> : <span className="text-[#978D78] text-xs">falta {eur(r.falta)}</span>}</td>
                </tr>
              ))}
              {data.leaderboard.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#978D78]">Sem volume no período.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AdminIncentivos() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [board, setBoard] = useState(null);
  const [popup, setPopup] = useState({ enabled: true, max_per_day: 5 });
  const [savingPopup, setSavingPopup] = useState(false);

  const load = () => adminApi.incentivosList().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    adminApi.incentivosPopupGet().then(setPopup).catch(() => {});
  }, []);

  const savePopup = async (next) => {
    setPopup(next);
    setSavingPopup(true);
    try { await adminApi.incentivosPopupSet(next); } catch { /* noop */ } finally { setSavingPopup(false); }
  };

  const save = async (body) => {
    if (editing?.id) await adminApi.incentivosUpdate(editing.id, body);
    else await adminApi.incentivosCreate(body);
    setEditing(null); await load();
  };
  const remove = async (id) => { if (window.confirm('Eliminar esta campanha?')) { await adminApi.incentivosDelete(id); load(); } };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-incentivos-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-[#0F1829] flex items-center gap-2"><Trophy size={22} className="text-amber-500" /> Campanhas de Incentivos</h1>
        <button onClick={() => setEditing({})} data-testid="incentivo-new-btn" className="inline-flex items-center gap-1.5 bg-[#0F1829] text-white px-3 py-2 rounded text-sm">
          <Plus size={16} /> Nova campanha
        </button>
      </div>

      <div className="mb-5 bg-white border border-[#F3F0E7] rounded-lg p-4" data-testid="incentivo-popup-settings">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-[#0F1829]" />
            <span className="text-sm font-semibold text-[#0F1829]">Popup promocional no CRM</span>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="incentivo-popup-enabled">
            <button type="button" role="switch" aria-checked={popup.enabled}
              onClick={() => savePopup({ ...popup, enabled: !popup.enabled })}
              className={`relative w-11 h-6 rounded-full transition-colors ${popup.enabled ? 'bg-emerald-500' : 'bg-[#D1D5DB]'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${popup.enabled ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
            {popup.enabled ? 'Ativo' : 'Desativado'}
          </label>
          <label className="flex items-center gap-2 text-sm text-[#374151]">
            Máximo por dia
            <input type="number" min={0} max={20} value={popup.max_per_day} data-testid="incentivo-popup-max"
              disabled={!popup.enabled}
              onChange={(e) => setPopup({ ...popup, max_per_day: e.target.value })}
              onBlur={(e) => savePopup({ ...popup, max_per_day: Math.max(0, Math.min(20, Number(e.target.value || 0))) })}
              className="w-20 border rounded px-2 py-1.5 disabled:opacity-50" />
          </label>
          <span className="text-xs text-[#978D78]">{savingPopup ? 'A guardar…' : 'Mostrado só a consultores, ao entrar no CRM, até ao limite de vezes por dia.'}</span>
        </div>
      </div>

      <div className="overflow-x-auto bg-white border border-[#F3F0E7] rounded-lg">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#978D78] border-b"><th className="p-3">Nome</th><th>Prémio</th><th>Âmbito</th><th className="text-right">Objetivo</th><th>Período</th><th>Contagem</th><th>Estado</th><th></th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-[#F1EEE6]" data-testid={`incentivo-row-${c.id}`}>
                <td className="p-3 font-medium text-[#0F1829]">{c.nome}</td>
                <td>{c.premio}</td>
                <td className="text-xs">{(c.ambito_labels || []).join(', ')}</td>
                <td className="text-right">{eur(c.valor_objetivo)}</td>
                <td className="text-xs">{c.data_inicio} → {c.data_fim}</td>
                <td className="text-xs">{c.modo_label || '—'}</td>
                <td>{c.ativo ? <span className="text-emerald-600 text-xs font-medium">Ativa</span> : <span className="text-[#978D78] text-xs">Inativa</span>}</td>
                <td className="text-right whitespace-nowrap pr-3">
                  <button onClick={() => setBoard(c.id)} title="Classificação" data-testid={`incentivo-board-${c.id}`} className="p-1.5 text-[#6B7280] hover:text-[#0F1829]"><ChartBar size={16} /></button>
                  <button onClick={() => setEditing(c)} title="Editar" className="p-1.5 text-[#6B7280] hover:text-[#0F1829]"><PencilSimple size={16} /></button>
                  <button onClick={() => remove(c.id)} title="Eliminar" className="p-1.5 text-red-400 hover:text-red-600"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#978D78]">Ainda não há campanhas. Crie a primeira.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <Form initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {board && <Leaderboard id={board} onClose={() => setBoard(null)} />}
    </div>
  );
}
